/* import __COLOCATED_TEMPLATE__ from './sender-selector.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { restartableTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import type InboxApi from 'embercom/services/inbox-api';
import type Session from 'embercom/services/session';
import type SenderEmailAddressSummary from 'embercom/objects/inbox/sender-email-address-summary';
import type AdminWithPermissions from 'embercom/objects/inbox/admin-with-permissions';
import {
  type ReplyChannel,
  replyChannelIsChat,
  replyChannelIsEmail,
} from 'embercom/objects/inbox/composer-pane';
import type IntlService from 'ember-intl/services/intl';
import { isEmpty } from '@ember/utils';

interface Args {
  admin: AdminWithPermissions;
  sender: SenderEmailAddressSummary | AdminWithPermissions;
  replyChannel: ReplyChannel;
  onSenderChange: (sender: any) => void;
  setCustomAddressExists: (customAddressExists: boolean) => void;
  recipientUserIds?: string[];
  onSenderBrandMismatch?: (hasMismatch: boolean) => void;
}

interface Signature {
  Args: Args;
}

export default class SenderSelector extends Component<Signature> {
  @service declare inboxApi: InboxApi;
  @service declare session: Session;
  @service declare intl: IntlService;

  @tracked senderEmailAddresses: (SenderEmailAddressSummary | AdminWithPermissions)[] = [];
  @tracked isLoading = false;
  @tracked senderEmailFilter = '';
  @tracked isBrandFiltered = false;
  @tracked hasBrandMismatch = false;
  @tracked hasCrossBrandConflict = false;

  private lastLoadedRecipientUserIds?: string[];
  private lastLoadWasBrandEnforced = false;

  readonly replyChannelIsChat = replyChannelIsChat;

  popover?: { hide: () => void; show: () => void };

  get isBrandEnforcementEnabled(): boolean {
    return (
      replyChannelIsEmail(this.args.replyChannel) &&
      this.session.workspace.isFeatureEnabled('outbound-email-brand-enforcement')
    );
  }

  get showBrandInvalid(): boolean {
    return this.isBrandEnforcementEnabled && this.hasBrandMismatch;
  }

  get brandTooltipContent(): string | undefined {
    if (!this.isBrandEnforcementEnabled) {
      return undefined;
    }
    if (this.hasCrossBrandConflict) {
      return this.intl.t('inbox.new-conversation.errors.sender-cross-brand-conflict');
    }
    if (this.hasBrandMismatch) {
      return this.intl.t('inbox.new-conversation.errors.sender-brand-mismatch');
    }
    return undefined;
  }

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    taskFor(this.loadSenderEmailAddresses).perform();
  }

  @restartableTask
  *loadSenderEmailAddresses(): TaskGenerator<void> {
    let recipientUserIds = this.isBrandEnforcementEnabled ? this.args.recipientUserIds : undefined;
    this.lastLoadedRecipientUserIds = this.args.recipientUserIds;
    this.lastLoadWasBrandEnforced = this.isBrandEnforcementEnabled;
    this.isLoading = true;
    let {
      addresses: customEmailAddresses,
      filteredByBrandId,
      hasCrossBrandConflict,
    } = (yield this.inboxApi.fetchSenderEmailAddresses(recipientUserIds)) as Awaited<
      ReturnType<InboxApi['fetchSenderEmailAddresses']>
    >;
    this.args.setCustomAddressExists(!!customEmailAddresses.length);

    if (this.isBrandEnforcementEnabled && hasCrossBrandConflict) {
      // Recipients belong to different brands — empty the dropdown and block send
      this.senderEmailAddresses = [];
      this.isBrandFiltered = false;
      this.hasCrossBrandConflict = true;
      this.hasBrandMismatch = true;
      this.args.onSenderBrandMismatch?.(true);
    } else if (this.isBrandEnforcementEnabled && filteredByBrandId != null) {
      // Single brand (or brand + no-brand) — filter to that brand's addresses
      this.senderEmailAddresses = [...customEmailAddresses];
      this.isBrandFiltered = true;
      this.hasCrossBrandConflict = false;
      let currentSenderId = this.args.sender?.id;
      let currentSenderInList = customEmailAddresses.some((addr) => addr.id === currentSenderId);
      let mismatch = !currentSenderInList;
      this.hasBrandMismatch = mismatch;
      this.args.onSenderBrandMismatch?.(mismatch);
    } else {
      this.senderEmailAddresses = [this.args.admin, ...customEmailAddresses];
      this.isBrandFiltered = false;
      this.hasCrossBrandConflict = false;
      this.hasBrandMismatch = false;
      this.args.onSenderBrandMismatch?.(false);
    }
    this.isLoading = false;
  }

  @action maybeReloadForRecipient() {
    // Notify parent whether brand enforcement blocks send on the current channel.
    // Brand state is preserved across channel switches — only the callback changes.
    if (!replyChannelIsEmail(this.args.replyChannel)) {
      this.args.onSenderBrandMismatch?.(false);
    } else if (this.hasBrandMismatch || this.hasCrossBrandConflict) {
      this.args.onSenderBrandMismatch?.(true);
    }

    let recipientsChanged = !this.recipientUserIdsMatch(
      this.args.recipientUserIds,
      this.lastLoadedRecipientUserIds,
    );
    // Reload when switching to email if the last load didn't include brand context
    let needsBrandReload =
      this.isBrandEnforcementEnabled &&
      !this.lastLoadWasBrandEnforced &&
      !!this.args.recipientUserIds?.length;

    if (recipientsChanged || needsBrandReload) {
      taskFor(this.loadSenderEmailAddresses).perform();
    }
  }

  @action setPopover(popover: { hide: () => void; show: () => void }) {
    this.popover = popover;
  }

  @action changeSender(newSender: any) {
    if (newSender.verified === false) {
      return;
    }

    this.args.onSenderChange(newSender);
    this.popover?.hide();
    this.senderEmailFilter = '';

    // Re-evaluate brand mismatch after sender change
    if (this.isBrandEnforcementEnabled && this.isBrandFiltered) {
      let isValid = this.senderEmailAddresses.some((addr) => addr.id === newSender.id);
      this.hasBrandMismatch = !isValid;
      this.args.onSenderBrandMismatch?.(!isValid);
    }
  }

  get currentReplyChannelIsChat(): boolean {
    return this.replyChannelIsChat(this.args.replyChannel);
  }

  get filteredSenderEmail() {
    if (isEmpty(this.senderEmailFilter)) {
      return this.senderEmailAddresses;
    }
    let regexp = new RegExp(this.senderEmailFilter, 'i');
    return this.senderEmailAddresses.filter(
      (sender) => (sender.email && regexp.test(sender.email)) || regexp.test(sender.name),
    );
  }

  private recipientUserIdsMatch(a?: string[], b?: string[]): boolean {
    if (a === b) {
      return true;
    }
    if (!a || !b || a.length !== b.length) {
      return false;
    }
    return a.every((id, i) => id === b[i]);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::NewConversation::SenderSelector': typeof SenderSelector;
    'inbox2/new-conversation/sender-selector': typeof SenderSelector;
  }
}
